import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateChatSnippetDTO,
  UpdateChatSnippetDTO,
  UpdateShopSettingsDTO,
} from "@/modules/chat-tools/domain/contracts";
import { chatToolsApi } from "./client";

const keys = {
  snippets: ["chat-tools", "snippets"] as const,
  shopSettings: ["chat-tools", "shop-settings"] as const,
  chatSummary: (orderId: string) =>
    ["chat-tools", "chat-summary", orderId] as const,
};

export function useChatSnippetsQuery() {
  return useQuery({
    queryKey: keys.snippets,
    queryFn: () => chatToolsApi.listSnippets(),
  });
}

export function useCreateChatSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChatSnippetDTO) =>
      chatToolsApi.createSnippet(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.snippets }),
  });
}

export function useUpdateChatSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateChatSnippetDTO;
    }) => chatToolsApi.updateSnippet(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.snippets }),
  });
}

export function useDeleteChatSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => chatToolsApi.deleteSnippet(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.snippets }),
  });
}

export function useShopSettingsQuery() {
  return useQuery({
    queryKey: keys.shopSettings,
    queryFn: () => chatToolsApi.getShopSettings(),
  });
}

export function useUpdateShopSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateShopSettingsDTO) =>
      chatToolsApi.updateShopSettings(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.shopSettings });
      qc.invalidateQueries({ queryKey: ["chat-tools", "chat-summary"] });
    },
  });
}

export function useOrderChatSummaryQuery(orderId: string) {
  return useQuery({
    queryKey: keys.chatSummary(orderId),
    queryFn: () => chatToolsApi.getOrderChatSummary(orderId),
    enabled: Boolean(orderId),
  });
}
